/**
 * ry-ui core — every component, the icon-registry API, and the runtime
 * side effects (`window.RyToast`, version log) with NO icon set pre-loaded.
 * The public entries (`ry-ui.ts` for Fomantic, `ry-ui-lucide.ts` for Lucide)
 * wrap this module and bolt on their chosen icon set via a side-effect
 * import. Consumers don't import this file directly.
 */

// Types
export type {
  ComponentState,
  StateChangeDetail,
  ChangeDetail,
  CleanupFn,
  ToastVariant,
  ToastOptions,
  SelectOption
} from './types.js';

// Core
export { RyElement } from './core/ry-element.js';
export { transform, processTransforms } from './core/ry-transform.js';

// Components
export { RyAccordion } from './components/ry-accordion.js';
export { RyModal } from './components/ry-modal.js';
export { RyTabs } from './components/ry-tabs.js';
export { RyDropdown } from './components/ry-dropdown.js';
export { RyButton } from './components/ry-button.js';
export { RySplit } from './components/ry-split.js';
export { RyThemeToggle } from './components/ry-theme-toggle.js';
export { RyCard } from './components/ry-card.js';
export { RyAlert } from './components/ry-alert.js';
export { RyField } from './components/ry-field.js';
export { RyFields } from './components/ry-fields.js';
export { RyForm, parseRules, type RuleFn, type RuleContext } from './components/ry-form.js';
export { RySwitch } from './components/ry-switch.js';
export { RyTooltip } from './components/ry-tooltip.js';
export { RyDrawer } from './components/ry-drawer.js';
export { RyToast } from './components/ry-toast.js';
export { RyProgress } from './components/ry-progress.js';
export { RyBreadcrumb } from './components/ry-breadcrumb.js';
export { RyLoader } from './components/ry-loader.js';
export { RyDivider } from './components/ry-divider.js';
export { RyList } from './components/ry-list.js';
export { RyPlaceholder } from './components/ry-placeholder.js';
export { RyRating } from './components/ry-rating.js';
export { RyStep } from './components/ry-step.js';
export { RySticky } from './components/ry-sticky.js';
export { RyImage } from './components/ry-image.js';
export { RyReveal } from './components/ry-reveal.js';
export { RyEmbed } from './components/ry-embed.js';
export { RyTable } from './components/ry-table.js';
export { RyNag } from './components/ry-nag.js';
export { RyPopover } from './components/ry-popover.js';
export { RyContextMenu } from './components/ry-context-menu.js';
export { RyHoverCard } from './components/ry-hover-card.js';
export { RySelect } from './components/ry-select.js';
export { RyCombobox } from './components/ry-combobox.js';
export { RyCode } from './components/ry-code.js';
export { RyExample } from './components/ry-example.js';
export { RyIcon } from './components/ry-icon.js';
export { RyToggleButton } from './components/ry-toggle-button.js';
export { RySlider } from './components/ry-slider.js';
export { RyKnob } from './components/ry-knob.js';
export { RyNumberSelect } from './components/ry-number-select.js';
export { RyColorPicker } from './components/ry-color-picker.js';
export type { ColorFormat, RGB, HSL, HSV } from './components/ry-color-picker.js';
export { RyColorInput } from './components/ry-color-input.js';
export { RyGradientPicker } from './components/ry-gradient-picker.js';
export type { GradientStop, GradientType, RadialShape } from './components/ry-gradient-picker.js';
export { RyTree, RyTreeItem } from './components/ry-tree.js';
export type { TreeNode } from './components/ry-tree.js';
export { RyTag } from './components/ry-tag.js';
export { RyTagInput } from './components/ry-tag-input.js';
export { RyHero } from './components/ry-hero.js';
export { RyStat } from './components/ry-stat.js';
export { RyFeature, RyFeatureGrid } from './components/ry-feature.js';
export { RyPricing, RyPricingCard } from './components/ry-pricing.js';
export { RyCarousel } from './components/ry-carousel.js';
export { RyThemePanel } from './components/ry-theme-panel.js';
export { RyTestimonial } from './components/ry-testimonial.js';
export { RyHeading } from './components/ry-heading.js';
export { RySearchList } from './components/ry-search-list.js';
export { RyLogoBar } from './components/ry-logo-bar.js';
export { RySidenav } from './components/ry-sidenav.js';
export { RyLogo } from './components/ry-logo.js';
export { RyTileRadio } from './components/ry-tile-radio.js';
export { RyTileCheckbox } from './components/ry-tile-checkbox.js';
export { RyCheckbox } from './components/ry-checkbox.js';
export { RyRadio } from './components/ry-radio.js';
export { RyRadioGroup } from './components/ry-radio-group.js';
export { RyButtonGroup } from './components/ry-button-group.js';
export { RyCalendar } from './components/ry-calendar.js';
export type { CalendarType, CalendarMode } from './components/ry-calendar.js';

// Ported Tier-2 utilities (Shoelace + daisyUI, MIT — see LICENSE-THIRD-PARTY.md)
export { RyFormatBytes } from './components/ry-format-bytes.js';
export { RyFormatDate } from './components/ry-format-date.js';
export { RyFormatNumber } from './components/ry-format-number.js';
export { RyRelativeTime } from './components/ry-relative-time.js';
export { RyCopyButton } from './components/ry-copy-button.js';
export { RyKbd } from './components/ry-kbd.js';
export { RyCountdown } from './components/ry-countdown.js';
export { RyStatus } from './components/ry-status.js';

// Ported Tier-1 components (Shoelace + daisyUI, MIT)
export { RyPagination } from './components/ry-pagination.js';
export { RyProgressRing } from './components/ry-progress-ring.js';
export { RyAvatar } from './components/ry-avatar.js';
export { RyBadge } from './components/ry-badge.js';
export { RyTimeline, RyTimelineItem } from './components/ry-timeline.js';
export { RyChat, RyChatBubble } from './components/ry-chat.js';

// Ported mockup chrome (daisyUI, MIT)
export { RyBrowserMockup } from './components/ry-browser-mockup.js';
export { RyWindowMockup } from './components/ry-window-mockup.js';
export { RyPhoneMockup } from './components/ry-phone-mockup.js';

// Ported Wave 4 — nav / overlay / compare / Fomantic primitives (MIT)
export { RyDock, RyDockItem } from './components/ry-dock.js';
export { RyFab, RyFabAction } from './components/ry-fab.js';
export { RyFilter, RyFilterOption } from './components/ry-filter.js';
export { RyStackOverlay } from './components/ry-stack-overlay.js';
export { RyNSplit } from './components/ry-n-split.js';
export { RyBento, bentoFlip } from './components/ry-bento.js';
export { RyImageComparer } from './components/ry-image-comparer.js';
export { RySegment, RySegments } from './components/ry-segment.js';
export { RyComment, RyComments } from './components/ry-comment.js';

// Data contract — docs/arch/data-contract.md
export type {
  RyRecord, RySortDir, RySortSpec, RyRange, RyRequest, RyResponse, RySource,
  RyMovePosition, RyMutations, RyDataState,
  RyChunk, RyPatch, RySubscription, RyStreamSource, RySubscribeSource, RySubscribeHandlers, RyAnySource,
} from './core/ry-data.js';
export { isAbort, isRetryable, requestKey, RyTimeoutError, RyHttpError } from './core/ry-data.js';
export { RyCoalescer, type CoalesceOptions } from './core/ry-coalesce.js';
export { applyChunk, applyPatch, sourceShape, paginationMode } from './core/ry-rows.js';

// Presence — docs/arch/presence-contract.md
export {
  createPresenceState, applyPeerPacket, dropPeer, reapExpired,
  colorFor, presenceKey, lockWinner, lockHolder, isDirty, peerIds,
  RY_PRESENCE_PALETTE,
} from './core/ry-presence.js';
export type {
  RyPresencePacket, RyPresenceState, RyPresenceDirty, RyPresencePoint, RyPresenceRect,
  RyPresenceLock, RyPeerCursor, RyPeerSelection, RyPeerDragPreview, RyPeerLock,
} from './core/ry-presence.js';
export { RyLoaderRunner, type RyRequestInit, type RyLoaderOptions } from './core/ry-loader-runner.js';
export { RySourceElement, resolveSource, type ResolvedSource } from './components/ry-source.js';
export {
  restSource, restParams, type RestOptions,
  railsSource, railsParams, RyRangeUnsupportedError, type RailsOptions,
  nestSource, nestParams, type NestOptions,
  cursorSource, cursorParams, type CursorOptions,
  postgrestSource, postgrestParams, postgrestHeaders, totalFromContentRange,
  totalFromHeaders, rowsFromBody, type AdapterOptions,
} from './adapters/ry-adapters.js';

// Ported from shadcn/ui (MIT) — see docs/workflows/port-shadcn.md
export { RyQuestionnaire, RyQuestion, RyChoice } from './components/ry-questionnaire.js';

// Icon registry API. The default-set side-effect import lives in the
// wrapper entries (`ry-ui.ts` / `ry-ui-lucide.ts`) — picking which glyphs
// ship belongs to the bundle layer, not core.
export { getIcon, registerIcon, registerIcons, registerSet, getIconNames, setIconSet, getIconSet, type IconSet } from './core/ry-icons.js';

// Make RyToast globally available for programmatic API
import { RyToast as _RyToast } from './components/ry-toast.js';

declare global {
  interface Window {
    RyToast: typeof _RyToast;
  }
}

window.RyToast = _RyToast;

// Log version
console.log('ry-ui loaded');
